import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Plus, Loader2, AlertTriangle, Trash2, ImageIcon, Upload } from 'lucide-react';
import { authFetch } from '@/lib/auth-client';
import { uploadAdminImage } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { localDatetimeToISO } from '@/lib/utils';
import { validatePrizeRows, buildPrizesPayload } from '@/lib/main-event-prizes';

const SPORT_OPTIONS = [
  { value: 'NFL', label: 'Football — NFL' },
  { value: 'NCAA_FOOTBALL', label: 'Football — NCAA' },
  { value: 'NBA', label: 'Basketball — NBA' },
  { value: 'NCAA_BASKETBALL', label: 'Basketball — NCAA' },
  { value: 'NHL', label: 'Hockey — NHL' },
  { value: 'SOCCER_EPL', label: 'Soccer — EPL' },
  { value: 'SOCCER_WORLD_CUP', label: 'Soccer — World Cup' },
  { value: 'SOCCER_UCL', label: 'Soccer — UCL' },
];
const EVENT_TYPES = ['BRACKET', 'ROUND_ROBIN', 'HEAD_TO_HEAD', 'SWISS'];

interface PrizeRow {
  placement: number;
  prizeType: 'COINS' | 'ITEM';
  coinAmount: number;
  itemName: string;
  itemImageUrl: string;
}

export default function AdminMainEventNewPage() {
  const router = useRouter();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
    sport: 'NCAA_BASKETBALL',
    eventType: 'BRACKET',
    format: 'STANDARD',
    accessType: 'OPEN',
    externalValidationUrl: '',
    startTime: '',
    endTime: '',
    entryFee: 0,
    highlighted: false,
    maxEntries: '' as string | number,
    respawnOnFull: false,
    durationClass: '',
    autoGenerateUndercards: true,
    prizePool: 0,
  });

  const [prizes, setPrizes] = useState<PrizeRow[]>([
    { placement: 1, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
    { placement: 2, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
    { placement: 3, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
  ]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
    }));
  };

  const handlePrizeChange = (index: number, field: keyof PrizeRow, value: string | number) => {
    setPrizes((prev) => prev.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const addPrize = () => {
    const nextPlacement = prizes.length > 0 ? Math.max(...prizes.map((p) => p.placement)) + 1 : 1;
    setPrizes((prev) => [...prev, { placement: nextPlacement, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' }]);
  };

  const removePrize = (index: number) => {
    setPrizes((prev) => prev.filter((_, i) => i !== index));
  };

  const coinTotal = prizes
    .filter((p) => p.prizeType === 'COINS')
    .reduce((sum, p) => sum + p.coinAmount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.prizePool > 0 && coinTotal > form.prizePool) {
      setError(`Coin prizes total (${coinTotal.toLocaleString()}) exceeds prize pool (${form.prizePool.toLocaleString()})`);
      return;
    }

    const prizeError = validatePrizeRows(prizes);
    if (prizeError) {
      setError(prizeError);
      return;
    }

    setCreating(true);
    setError('');

    // Shared builder — keeps the payload identical to the Next.js admin.
    const prizesPayload = buildPrizesPayload(prizes);

    // Also build legacy prizeDistribution for backward compat
    const prizeDistribution: Record<string, number> = {};
    for (const p of prizes) {
      if (p.prizeType === 'COINS' && p.coinAmount > 0) {
        prizeDistribution[String(p.placement)] = p.coinAmount;
      }
    }

    try {
      const res = await authFetch('/api/admin/main-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          maxEntries: form.maxEntries === '' ? null : Number(form.maxEntries),
          durationClass: form.durationClass || null,
          imageUrl: form.imageUrl || null,
          startTime: localDatetimeToISO(form.startTime),
          endTime: localDatetimeToISO(form.endTime),
          prizeDistribution: Object.keys(prizeDistribution).length > 0 ? prizeDistribution : null,
          prizes: prizesPayload,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        const eventId = data.data?.id || data.id;
        if (form.autoGenerateUndercards) {
          // Best-effort: empty body → week range derives from event length.
          // No schedule for this sport (or any failure) just lands the admin
          // on the edit page, where the Generate panel can retry.
          try {
            await authFetch(`/api/admin/main-events/${eventId}/undercards/generate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
            });
          } catch {
            // fall through to the edit page
          }
        }
        router.push(`/admin/main-events/${eventId}`);
      } else {
        setError(data.error || 'Failed to create event');
      }
    } catch {
      setError('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none [color-scheme:dark]';
  const smallInputClass = 'w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-red-500 focus:outline-none';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/admin/main-events"
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">Create Main Event</h1>
          <p className="text-sm text-zinc-400">Add a new pick-em event</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Title *</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="e.g., March Madness 2026"
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                placeholder="Event description..."
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Banner Image URL <span className="text-zinc-600 font-normal">(1200×400 recommended)</span></label>
              <div className="flex gap-3">
                <input
                  type="url"
                  name="imageUrl"
                  value={form.imageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/banner.jpg"
                  className={inputClass}
                />
              </div>
              {form.imageUrl && (
                <div className="mt-2 rounded-lg overflow-hidden border border-zinc-700">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={form.imageUrl}
                    alt="Banner preview"
                    className="w-full h-32 object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
              {!form.imageUrl && (
                <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                  <ImageIcon className="h-3.5 w-3.5" />
                  Displayed as hero banner on the event page
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Sport *</label>
              <select
                name="sport"
                value={form.sport}
                onChange={handleChange}
                className={inputClass}
              >
                {SPORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="highlighted"
                type="checkbox"
                checked={form.highlighted}
                onChange={(e) => setForm((f) => ({ ...f, highlighted: e.target.checked }))}
                className="h-4 w-4 accent-amber-500"
              />
              <label htmlFor="highlighted" className="text-sm text-zinc-400">
                Highlight this event{' '}
                <span className="text-zinc-500">(features it in home &amp; events carousels)</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">
                Max entries <span className="text-zinc-500">(blank = uncapped)</span>
              </label>
              <input
                type="number"
                min={1}
                name="maxEntries"
                value={form.maxEntries}
                onChange={handleChange}
                className={inputClass}
                placeholder="Uncapped"
              />
              <label className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
                <input
                  type="checkbox"
                  checked={form.respawnOnFull}
                  onChange={(e) => setForm((f) => ({ ...f, respawnOnFull: e.target.checked }))}
                  className="h-4 w-4 accent-amber-500"
                />
                Auto-create the next event when full
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Format</label>
              <select
                name="format"
                value={form.format}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="STANDARD">Standard (points)</option>
                <option value="SURVIVOR">Survivor (weekly elimination)</option>
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Event Type</label>
              <select
                name="eventType"
                value={form.eventType}
                onChange={handleChange}
                className={inputClass}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Event Length</label>
              <select
                name="durationClass"
                value={form.durationClass}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Not set</option>
                <option value="WEEKLY">Weekly (one week)</option>
                <option value="MONTHLY">Monthly (~4 weeks)</option>
                <option value="SEASON">Seasonal (full season)</option>
              </select>
              <p className="text-xs text-zinc-500 mt-1">
                Sets the default week range when auto-generating undercards.
              </p>
              <label className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
                <input
                  type="checkbox"
                  checked={form.autoGenerateUndercards}
                  onChange={(e) => setForm((f) => ({ ...f, autoGenerateUndercards: e.target.checked }))}
                  className="h-4 w-4 accent-amber-500"
                />
                Auto-generate undercards from the season schedule{' '}
                <span className="text-zinc-500">(sports with a schedule — NFL)</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee (coins)</label>
              <input
                type="number"
                name="entryFee"
                value={form.entryFee}
                onChange={handleChange}
                min={0}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Access Type</label>
              <select
                name="accessType"
                value={form.accessType}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="OPEN">Open (anyone can join)</option>
                <option value="CODE_REQUIRED">Code Required</option>
              </select>
            </div>
            {form.accessType === 'CODE_REQUIRED' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-400 mb-1">External Validation URL (optional)</label>
                <input
                  type="url"
                  name="externalValidationUrl"
                  value={form.externalValidationUrl}
                  onChange={handleChange}
                  placeholder="https://partner-api.com/validate-code"
                  className={inputClass}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  If set, codes are validated via POST to this URL. Otherwise, upload codes directly.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Start Time *</label>
              <input
                type="datetime-local"
                name="startTime"
                value={form.startTime}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">End Time *</label>
              <input
                type="datetime-local"
                name="endTime"
                value={form.endTime}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Prizes */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Prizes</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-400 mb-1">Total Prize Pool (coins, for reference)</label>
            <input
              type="number"
              name="prizePool"
              value={form.prizePool}
              onChange={handleChange}
              min={0}
              placeholder="0"
              className={inputClass}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-zinc-400">Prize by Placement</label>
              {form.prizePool > 0 && (
                <span className={`text-xs ${coinTotal > form.prizePool ? 'text-red-400' : 'text-zinc-500'}`}>
                  {coinTotal.toLocaleString()} / {form.prizePool.toLocaleString()} coins allocated
                </span>
              )}
            </div>

            {prizes.map((prize, i) => (
              <div key={i} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-16">
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Place</label>
                    <input
                      type="number"
                      value={prize.placement}
                      onChange={(e) => handlePrizeChange(i, 'placement', parseInt(e.target.value) || 1)}
                      min={1}
                      className={smallInputClass}
                    />
                  </div>
                  <div className="w-28">
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Type</label>
                    <select
                      value={prize.prizeType}
                      onChange={(e) => handlePrizeChange(i, 'prizeType', e.target.value)}
                      className={smallInputClass}
                    >
                      <option value="COINS">Coins</option>
                      <option value="ITEM">Item</option>
                    </select>
                  </div>
                  {prize.prizeType === 'COINS' ? (
                    <div className="flex-1">
                      <label className="block text-[10px] text-zinc-500 mb-0.5">Amount</label>
                      <input
                        type="number"
                        value={prize.coinAmount}
                        onChange={(e) => handlePrizeChange(i, 'coinAmount', parseInt(e.target.value) || 0)}
                        min={0}
                        placeholder="0"
                        className={smallInputClass}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <label className="block text-[10px] text-zinc-500 mb-0.5">Item Name</label>
                        <input
                          type="text"
                          value={prize.itemName}
                          onChange={(e) => handlePrizeChange(i, 'itemName', e.target.value)}
                          placeholder="e.g., Gold Trophy Card"
                          className={smallInputClass}
                        />
                      </div>
                    </>
                  )}
                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={() => removePrize(i)}
                      className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {prize.prizeType === 'ITEM' && (
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Item Image <span className="text-zinc-600">(200×200, square)</span></label>
                    <div className="flex gap-2 items-center">
                      <label className="flex items-center gap-1 px-2 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-xs cursor-pointer transition-colors shrink-0">
                        <Upload className="h-3 w-3" />
                        Upload
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const { url } = await uploadAdminImage(file, 'prizes');
                              handlePrizeChange(i, 'itemImageUrl', url);
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Upload failed');
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <input
                        type="url"
                        value={prize.itemImageUrl}
                        onChange={(e) => handlePrizeChange(i, 'itemImageUrl', e.target.value)}
                        placeholder="...or paste URL"
                        className={smallInputClass}
                      />
                    </div>
                    {prize.itemImageUrl && (
                      <div className="mt-1">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={prize.itemImageUrl}
                          alt="Prize preview"
                          className="w-10 h-10 rounded object-cover border border-zinc-700"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addPrize}
              className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Add prize tier
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Link
            href="/admin/main-events"
            className="text-zinc-400 hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <Button type="submit" disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" />
                Create Event
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
